/**
 * Copyright Mántum All Rights Reserved.
 */
import * as chrono from 'chrono-node';
import Internal from '../internal/input';
import useOpen from '../../../hooks/useOpen';
import useOutside from '../../../hooks/useOutside';
import { ACTION_VALUE, useGroup } from '../../group';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { PropTypes, getValue } from '../internal/datetime/prop-types';
import { Suspense, forwardRef, lazy, useEffect, useRef, useState } from 'react';
import { es } from 'date-fns/locale';
import { formatter } from '@mantum/kanela';
import { getStyle } from './style';
import { language } from '../../../language';

import type { FormatDate as Format } from '@mantum/kanela';

const KEY_TEMPLATE = 'YYYY-MM-DD';

const Picker = lazy(() => {
    return import('./library');
});

export default forwardRef<HTMLInputElement, PropTypes<Format>>(
    function Component(props, ref) {
        // prettier-ignore
        const {
            format = 'YYYY-MM-DD',
            name
        } = props

        const changeRef = useRef(props.onChange);
        const containerRef = useRef<HTMLDivElement>(null);

        const locale = props.advanced?.locale ?? language();
        const value = getValue(formatter.date, format, props.value);

        const groups = useGroup();
        const [currentValue, setCurrentValue] = useState(value);

        const classNames = getStyle('single');

        // prettier-ignore
        const {
            onClose,
            onOpen,
            value: isOpen,
        } = useOpen(false);

        useOutside(containerRef, () => {
            onClose();
        });

        useEffect(() => {
            const current = getValue(formatter.date, format, props.value);
            setCurrentValue(current);

            if (changeRef.current) {
                if (current.value === '') {
                    changeRef.current(name, '');
                    return;
                }

                changeRef.current(
                    name,
                    formatter.date.format(current.date, KEY_TEMPLATE)
                );
            }
        }, [name, format, props.value]);

        const setValue = (date?: Date) => {
            if (date) {
                const value = formatter.date.format(date, format);
                setCurrentValue(() => ({
                    date,
                    value: value ? value : ''
                }));

                if (changeRef.current) {
                    if (value === '') {
                        changeRef.current(name, '');
                        return;
                    }

                    changeRef.current(
                        name,
                        formatter.date.format(date, KEY_TEMPLATE)
                    );
                }
            }
        };

        const handleSelect = (date?: Date) => {
            const current = date ? date : currentValue.date;
            setValue(current);
            onClose();
        };

        const handleChange = (_?: string, value?: unknown) => {
            setCurrentValue((previous) => ({
                ...previous,
                value: value as string
            }));
        };

        const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
            let target = null;
            const value = event.target.value;

            if (value) {
                target = chrono[locale].parseDate(value);
                setValue(target);
            }

            if (!isOpen && props.onBlur && changeRef.current) {
                if (!target) {
                    changeRef.current(name, '');
                    props.onBlur(event);
                    return;
                }

                const value = formatter.date.format(target, format);
                event.target.value = value ? value : '';

                if (value === '') {
                    changeRef.current(name, '');
                    return;
                }

                changeRef.current(
                    name,
                    formatter.date.format(target, KEY_TEMPLATE)
                );
            }

            props.onBlur && props.onBlur(event);
        };

        useEffect(() => {
            if (!name || !groups) {
                return;
            }

            const temporal =
                name.includes('-date') && !(name in groups)
                    ? name.substring(0, name.length - 5)
                    : name;

            if (!(temporal in groups)) {
                return;
            }

            const content = groups[temporal];
            if (content.action === ACTION_VALUE) {
                const next = content.value as string | Date | undefined;
                const current = getValue(formatter.date, format, next);

                setCurrentValue(current);
                if (changeRef.current) {
                    if (current.value === '') {
                        changeRef.current(temporal, '');
                        return;
                    }

                    changeRef.current(
                        temporal,
                        formatter.date.format(current.date, KEY_TEMPLATE)
                    );
                }
            }
        }, [name, groups, format]);

        return (
            <div className='relative w-full'>
                <Internal
                    autocomplete='off'
                    disabled={props.disabled}
                    error={props.error}
                    name={props.name}
                    onBlur={onBlur}
                    onChange={handleChange}
                    placeholder={props.placeholder}
                    ref={ref}
                    testid={props.testid}
                    type='text'
                    value={currentValue.value}
                />

                <div ref={containerRef}>
                    {!props.disabled && (
                        <button
                            aria-label='Open calendar'
                            className='absolute right-2 top-2 cursor-pointer outline-none'
                            data-testid={`${props.testid}-action`}
                            tabIndex={-1}
                            type='button'
                        >
                            <CalendarIcon
                                className='h-6 w-6'
                                onClick={onOpen}
                            />
                        </button>
                    )}

                    {!props.disabled && isOpen && (
                        <Suspense>
                            <div className='absolute z-50 mt-2 rounded bg-white shadow'>
                                <Picker
                                    classNames={classNames}
                                    defaultMonth={currentValue.date}
                                    locale={locale === 'es' ? es : undefined}
                                    mode='single'
                                    onSelect={handleSelect}
                                    selected={currentValue.date}
                                />
                            </div>
                        </Suspense>
                    )}
                </div>
            </div>
        );
    }
);
